import React, { useEffect, useMemo, useState } from 'react';

const getCaseMessagePreview = (caseItem) => {
  const sourceMessages = Array.isArray(caseItem?.messages) ? caseItem.messages : [];
  const cleaned = sourceMessages
    .map((msg) => String(msg || '').trim())
    .filter((msg) => msg.length > 0);
  if (cleaned.length === 0) return [];
  return cleaned.slice(0, 3);
};

/**
 * Pop-up case picker for the API Test plan runner.
 *
 * Lets the user toggle which test cases will be included in the next run.
 * Supports bulk shortcuts: All / None / Only failed last run / Only not-skipped.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Function} props.onConfirm  - Called with `number[]` (selected case indices) when the user clicks "Run selected".
 * @param {Array<Object>} props.cases - The plan's cases (in order).
 * @param {Array<number>} props.initialSelection - Indices that should be checked when the modal opens.
 * @param {Set<number>|Array<number>} [props.failedLastRunIndices] - Indices that failed in the most recent run (for "Only failed last run").
 * @param {string} [props.planName]
 */
const ApiTestCasePickerModal = ({
  isOpen,
  onClose,
  onConfirm,
  cases,
  initialSelection,
  failedLastRunIndices,
  planName,
}) => {
  const safeCases = useMemo(() => (Array.isArray(cases) ? cases : []), [cases]);
  const failedSet = useMemo(() => {
    if (failedLastRunIndices instanceof Set) return failedLastRunIndices;
    if (Array.isArray(failedLastRunIndices)) return new Set(failedLastRunIndices);
    return new Set();
  }, [failedLastRunIndices]);

  const [selected, setSelected] = useState(() => new Set(initialSelection || []));

  useEffect(() => {
    if (!isOpen) return;
    setSelected(new Set(initialSelection || []));
  }, [isOpen, initialSelection]);

  if (!isOpen) return null;

  const totalCases = safeCases.length;
  const selectedArray = Array.from(selected).sort((a, b) => a - b);
  const selectedCount = selectedArray.length;
  const allIndices = safeCases.map((_, idx) => idx);
  const notSkippedIndices = safeCases
    .map((c, idx) => (!c?.is_skipped ? idx : null))
    .filter((idx) => idx !== null);
  const failedIndicesInPlan = allIndices.filter((idx) => failedSet.has(idx));

  const toggleOne = (idx) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const setAll = () => setSelected(new Set(allIndices));
  const setNone = () => setSelected(new Set());
  const setOnlyFailed = () => setSelected(new Set(failedIndicesInPlan));
  const setOnlyNotSkipped = () => setSelected(new Set(notSkippedIndices));

  const handleConfirm = () => {
    if (typeof onConfirm === 'function') {
      onConfirm(selectedArray);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[80vh] flex flex-col rounded-xl bg-white shadow-2xl border border-gray-200 overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 bg-gray-50">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900">Choose cases to run</h3>
            <div className="text-xs text-gray-500 truncate">
              {planName ? `Plan: ${planName}` : 'Pick which cases to include in the next run.'}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl leading-none px-2"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-gray-200 bg-white">
          <span className="text-xs text-gray-500 mr-1">Quick select:</span>
          <button
            type="button"
            onClick={setAll}
            className="px-2.5 py-1 text-xs rounded border border-gray-200 bg-white text-gray-700 hover:bg-gray-100"
          >
            All ({totalCases})
          </button>
          <button
            type="button"
            onClick={setNone}
            className="px-2.5 py-1 text-xs rounded border border-gray-200 bg-white text-gray-700 hover:bg-gray-100"
          >
            None
          </button>
          <button
            type="button"
            onClick={setOnlyNotSkipped}
            className="px-2.5 py-1 text-xs rounded border border-gray-200 bg-white text-gray-700 hover:bg-gray-100"
          >
            Only not-skipped ({notSkippedIndices.length})
          </button>
          <button
            type="button"
            onClick={setOnlyFailed}
            disabled={failedIndicesInPlan.length === 0}
            className="px-2.5 py-1 text-xs rounded border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            title={
              failedIndicesInPlan.length > 0
                ? 'Select only cases that failed in the last run.'
                : 'No last-run failures available.'
            }
          >
            Only failed last run ({failedIndicesInPlan.length})
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-2 py-2 bg-white">
          {totalCases === 0 ? (
            <div className="text-center text-sm text-gray-500 py-8">
              This plan has no cases.
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {safeCases.map((caseItem, idx) => {
                const checked = selected.has(idx);
                const label = caseItem?.name || `Case ${idx + 1}`;
                const isSkipped = !!caseItem?.is_skipped;
                const failedLast = failedSet.has(idx);
                const messagePreview = getCaseMessagePreview(caseItem);
                const hasMoreMessages = (Array.isArray(caseItem?.messages) ? caseItem.messages : []).filter(
                  (msg) => String(msg || '').trim().length > 0
                ).length > messagePreview.length;
                return (
                  <li key={`${label}-${idx}`}>
                    <label
                      className={`flex items-start gap-3 px-3 py-2 cursor-pointer rounded hover:bg-gray-50 ${
                        checked ? 'bg-[#E7F5EC]/60' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-[#00A884] focus:ring-[#00A884]"
                        checked={checked}
                        onChange={() => toggleOne(idx)}
                      />
                      <span className="text-xs text-gray-400 w-8 text-right pt-0.5">{idx + 1}.</span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm text-gray-800 truncate">{label}</span>
                        {messagePreview.length > 0 && (
                          <span className="mt-1 block space-y-0.5">
                            {messagePreview.map((msg, msgIdx) => (
                              <span key={msgIdx} className="block text-xs text-gray-500 truncate">
                                {msgIdx + 1}. {msg}
                              </span>
                            ))}
                            {hasMoreMessages && (
                              <span className="block text-[11px] text-gray-400">
                                ...more messages
                              </span>
                            )}
                          </span>
                        )}
                      </span>
                      <span className="flex items-center gap-1">
                        {isSkipped && (
                          <span className="px-1.5 py-0.5 text-[10px] rounded bg-gray-200 text-gray-600">
                            Skipped
                          </span>
                        )}
                        {failedLast && (
                          <span className="px-1.5 py-0.5 text-[10px] rounded bg-red-100 text-red-700">
                            Failed last run
                          </span>
                        )}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-600">
            <span className="font-medium text-gray-900">{selectedCount}</span> of {totalCases} selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-xs rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={selectedCount === 0}
              className="px-3 py-1.5 text-xs rounded bg-[#00A884] text-white hover:bg-[#06CF9C] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Run selected ({selectedCount})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiTestCasePickerModal;
